// apps/web/src/services/informeAvanceService.ts

import { toISODateSafe } from '../utils/dateUtils';

// ===============================
// INTERFACES
// ===============================

export interface InformeAvanceRequest {
  fecha_desde: string;
  fecha_hasta: string;
  predios_ids: number[];
  zoom?: number;
  titulo?: string;
  incluir_datos_area?: boolean;
  incluir_mapas?: boolean;
  lang?: string;
  formato?: FormatoGeo;
  fundir?: boolean;
}

export type FormatoGeo = 'kml' | 'kmz';

export interface ReporteDetalle {
  numero: string;
  fecha: Date;
  fecha_str: string;
  maquina_descripcion: string;
  operador_descripcion: string;
  horometro_inicial: number;
  horometro_final: number;
  faena_descripcion: string;
  horas: number;
  // Tres áreas de distinta naturaleza (ha): declarada por el operador (manual),
  // del KML (geometría real) y neta (aporte contra lo ya ingresado antes).
  // kml y neta son null para faenas sin KML (sin geometría).
  area_declarada: number;
  area_kml: number | null;
  area_neta: number | null;
  rendimiento_declarada: number;
  rendimiento_kml: number | null;
  rendimiento_neta: number | null;
  // Superposición geométrica del KML contra otro reporte ingresado antes
  // (= KML − neta). Ver superposicion_service en el backend.
  tiene_superposicion?: boolean;
  superposicion_area_hectareas?: number;
}

export interface CombustibleDetalle {
  numero: string;
  fecha: Date;
  fecha_str: string;
  maquina_descripcion: string;
  operador_descripcion: string;
  horometro_inicial: number;
  cantidad: number;
  unidad: string;
}

export interface InformePredio {
  predio: {
    id: number;
    codigo: string;
    descripcion: string;
    propietario?: string;
    superficie_ha?: number;
  };
  metricas: {
    resumen: {
      total_reportes: number;
      total_horas: number;
      total_area_hectareas: number;
      area_promedio_por_reporte: number;
      horas_promedio_por_reporte: number;
    };
  };
  reportes: ReporteDetalle[];
  combustible: CombustibleDetalle[];
  geometrias: any[];
  centro_mapa: {
    latitud: number;
    longitud: number;
  };
}

export interface InformeAvanceDatos {
  success: boolean;
  periodo: {
    fecha_desde: string;
    fecha_hasta: string;
  };
  reportes_por_predio: InformePredio[];
  combustible_por_predio?: CombustibleDetalle[][];
}

// ===============================
// SERVICIO
// ===============================

export class InformeAvanceService {

  /**
   * Obtiene datos del informe de avance sin generar PDF
   */
  static async obtenerDatosInformeAvance(request: Omit<InformeAvanceRequest, 'zoom' | 'titulo'>): Promise<InformeAvanceDatos> {
    try {
      const url = '/mava/api/v1/informes/informe-avance-datos';

      // Obtener token de autenticación
      const token = localStorage.getItem('mava_token');
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      // Obtener empresa activa
      const empresaActivaStr = localStorage.getItem('mava_empresa_activa');
      const empresaActiva = empresaActivaStr ? JSON.parse(empresaActivaStr) : null;

      // Hacer request POST
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Empresa-ID': empresaActiva?.empresa?.id?.toString() || '1',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData: any = {};
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText };
        }
        throw new Error(errorData.detail || errorData.message || `Error HTTP: ${response.status}`);
      }

      return await response.json();

    } catch (error: any) {
      console.error('❌ Error obteniendo datos informe de avance:', error);
      throw error;
    }
  }

  /**
   * Genera CSV de informe de avance por múltiples predios y fechas
   */
  static async generarInformeAvanceCSV(request: Omit<InformeAvanceRequest, 'zoom' | 'titulo'>): Promise<void> {
    try {
      const url = '/mava/api/v1/informes/informe-avance-csv';

      // Obtener token de autenticación
      const token = localStorage.getItem('mava_token');
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      // Obtener empresa activa
      const empresaActivaStr = localStorage.getItem('mava_empresa_activa');
      const empresaActiva = empresaActivaStr ? JSON.parse(empresaActivaStr) : null;

      // Hacer request POST
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Empresa-ID': empresaActiva?.empresa?.id?.toString() || '1',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData: any = {};
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText };
        }
        throw new Error(errorData.detail || errorData.message || `Error HTTP: ${response.status}`);
      }

      // Convertir respuesta a blob
      const blob = await response.blob();

      // Crear enlace de descarga
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `informe_avance_${toISODateSafe(new Date())}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Limpiar URL temporal
      URL.revokeObjectURL(link.href);

    } catch (error: any) {
      console.error('❌ Error generando CSV informe de avance:', error);
      throw error;
    }
  }

  /**
   * Genera archivo geográfico (KML o KMZ) con las geometrías de los predios seleccionados
   */
  static async generarInformeAvanceGeo(
    request: Omit<InformeAvanceRequest, 'zoom' | 'formato'>,
    formato: FormatoGeo = 'kml',
    fundir: boolean = false
  ): Promise<void> {
    try {
      const url = '/mava/api/v1/informes/informe-avance-kml';

      // Obtener token de autenticación
      const token = localStorage.getItem('mava_token');
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      // Obtener empresa activa
      const empresaActivaStr = localStorage.getItem('mava_empresa_activa');
      const empresaActiva = empresaActivaStr ? JSON.parse(empresaActivaStr) : null;

      // Hacer request POST
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Empresa-ID': empresaActiva?.empresa?.id?.toString() || '1',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...request, formato, fundir })
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData: any = {};
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText };
        }
        throw new Error(errorData.detail || errorData.message || `Error HTTP: ${response.status}`);
      }

      // Convertir respuesta a blob
      const blob = await response.blob();

      // Crear enlace de descarga
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `informe_avance_${toISODateSafe(new Date())}.${formato}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Limpiar URL temporal
      URL.revokeObjectURL(link.href);

    } catch (error: any) {
      console.error(`❌ Error generando ${formato.toUpperCase()} informe de avance:`, error);
      throw error;
    }
  }

  /**
   * Genera PDF de informe de avance por predio y fechas
   * Sigue el mismo patrón que AnalisisView para la generación de PDFs
   */
  static async generarInformeAvancePDF(request: InformeAvanceRequest): Promise<void> {
    try {
      const url = '/mava/api/v1/informes/informe-avance-pdf';

      // Obtener token de autenticación
      const token = localStorage.getItem('mava_token');
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      // Obtener empresa activa
      const empresaActivaStr = localStorage.getItem('mava_empresa_activa');
      const empresaActiva = empresaActivaStr ? JSON.parse(empresaActivaStr) : null;

      // Hacer request POST
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Empresa-ID': empresaActiva?.empresa?.id?.toString() || '1',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        // Intentar leer el error como JSON primero
        const errorText = await response.text();

        let errorData: any = {};
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          // Si no es JSON, usar el texto directamente
          errorData = { message: errorText };
        }

        throw new Error(errorData.detail || errorData.message || `Error HTTP: ${response.status}`);
      }

      // Verificar que la respuesta sea un PDF
      const contentType = response.headers.get('content-type');

      if (!contentType || !contentType.includes('application/pdf')) {
        // Leer el contenido para debugging
        await response.text();
        throw new Error(`La respuesta no es un PDF válido. Content-Type: ${contentType}`);
      }

      // Convertir respuesta a blob
      const blob = await response.blob();

      // Crear enlace de descarga
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `informe_avance_${toISODateSafe(new Date())}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Limpiar URL temporal
      URL.revokeObjectURL(link.href);

    } catch (error: any) {
      console.error('❌ Error generando informe de avance:', error);
      throw error;
    }
  }
}

export default InformeAvanceService;
